/** @format */

import {
  Chain,
  Environment,
  ProductCatalogInput,
  StoreDetails,
} from '@shared/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import {
  HeaderLocationService,
  HttpService,
  Logger,
  StoreService,
} from '@shared/data-access/services';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Injectable,
  PLATFORM_ID,
  StateKey,
  TransferState,
  computed,
  inject,
  isDevMode,
  makeStateKey,
} from '@angular/core';
import { Observable, ReplaySubject, map, of, shareReplay, throwError, zip } from 'rxjs';
import { ProductList, ProductListApiParams } from '@plp/data-access/interfaces';
import { catchError, retry, tap, timeout } from 'rxjs/operators';
import {
  selectMlBypass,
  selectSelectedSort,
  selectSnbAudience,
  snbAudienceChanged,
} from '@plp/data-access/state';
import { selectPageType, selectUserZipCode } from '@state/data-access';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { DEFAULT_PAGE_SIZE } from '@plp/data-access/constants';
import { Params } from '@angular/router';
import { Store } from '@ngrx/store';
import { isPlatformBrowser } from '@angular/common';

/** Identifies the location that triggered a SearchVO API error log. */
export type ProductListApiErrorSource =
  | 'srlp-guard'
  | 'plp-guard'
  | 'plp-guard:second-pass'
  | 'plp-effects';

interface ProductListApiErrorLike {
  status?: number | string;
  message?: string;
  name?: string;
  stack?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductListApiService {
  private readonly chain = inject(Chain);
  private readonly http = inject(HttpClient);
  private readonly httpService = inject(HttpService);
  private readonly environment = inject(Environment);
  private readonly store = inject(Store);
  private readonly storeService = inject(StoreService);
  private readonly headerLocationService = inject(HeaderLocationService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly transferState = inject(TransferState);
  private readonly logger = inject(Logger);

  private readonly pageType = this.store.selectSignal(selectPageType);
  private readonly userZipCode = this.store.selectSignal(selectUserZipCode);
  private readonly mlBypass = this.store.selectSignal(selectMlBypass);
  private readonly snbAudience = this.store.selectSignal(selectSnbAudience);
  private readonly selectedSort = this.store.selectSignal(selectSelectedSort);

  private readonly secondPassReady = new ReplaySubject<void>(1);

  isFamilyPage = computed(() => this.pageType()?.toLowerCase() === 'plp');
  // We need to get the zipcode from cookie for srlp/csr intial page load.
  currentUserZipCode = computed(
    () =>
      this.userZipCode() ||
      this.headerLocationService.getDeliveryZipCookie() ||
      this.headerLocationService.getWhereaboutsCookie() ||
      '',
  );

  /**
   * @description takes filters out of the url and reformats the filter data for the state store and UI components can consume
   * @example filterFacets=X_BRAND:PUMA to this {x_brand:['Puma']}
   */
  formatFiltersFromURL(rawFilterData: string): Record<string, string[]> {
    const filters = rawFilterData;
    const filterInfoMap: Record<string, string[]> = {};

    if (filters) {
      const filterFacets = filters.split(';');

      filterFacets.forEach((facetInfo) => {
        const facets = facetInfo.split(':');

        if (facets.length === 2) {
          filterInfoMap[facets[0]] = facets[1].split(',');
        }
      });
    }

    return filterInfoMap;
  }

  setProductListParamDataForApi(params: ProductListApiParams): ProductCatalogInput {
    const {
      selectedCategoryId,
      searchTerm,
      pageNumber = 0,
      selectedFilters = null,
      pageSize = DEFAULT_PAGE_SIZE,
      includeFulfillmentFacets = false,
      selectedStoreNumber = '',
      zipcode = '',
    } = params;
    let dataFromStoreCookieString: StoreDetails | undefined | null = undefined;

    // looking for zipcode and store on the cookie, important for first load
    if (!selectedStoreNumber || !zipcode) {
      const storeCookieString = this.storeService.getSavedCustomerStore() ?? '';
      dataFromStoreCookieString =
        this.storeService.getStoreDataFromStoreCookieString(storeCookieString);
    }

    const queryParams: ProductCatalogInput = {
      pageNumber,
      pageSize,
      selectedSort: this.selectedSort().value || 0,
      selectedStore: selectedStoreNumber || dataFromStoreCookieString?.storeNumber || '',
      storeId: this.chain.storeId,
      zipcode: zipcode || dataFromStoreCookieString?.storeZip || '',
      isFamilyPage: this.isFamilyPage(),
      mlBypass: this.mlBypass(),
      snbAudience: this.getSnbAudience(),
      includeFulfillmentFacets,
    };

    // So we can reproduce issues related to zip + store cookies not being present server-side
    if (!isPlatformBrowser(this.platformId) && isDevMode()) {
      queryParams.zipcode = '';
      queryParams.selectedStore = '';
    }

    if (selectedFilters !== null && Object.keys(selectedFilters).length) {
      queryParams['selectedFilters'] = selectedFilters;
      // if selectedFilters is true, check if selected filter Same Day Delivey, set delivery location zipcode
      if (
        selectedFilters.facetStore?.length > 0 &&
        selectedFilters.facetStore.includes('SDD') &&
        !includeFulfillmentFacets
      ) {
        queryParams['zipcode'] = this.currentUserZipCode();
      }
    }

    if (this.isFamilyPage()) {
      const selectedCategory = `${this.chain.catalogIdSales}_${selectedCategoryId}`;
      queryParams['selectedCategory'] = selectedCategory;
    } else if (searchTerm) {
      queryParams['searchTerm'] = searchTerm;
    }

    const zipcodeParam = queryParams.zipcode;
    // Limit zipcode to first 5 characters
    if (zipcodeParam && (zipcodeParam.length ?? 0) > 5) {
      queryParams.zipcode = zipcodeParam.slice(0, 5);
    }

    if (includeFulfillmentFacets && ['dsg', 'gg'].includes(this.chain.chainIdentifierAbbr)) {
      queryParams['sddZipcode'] = this.currentUserZipCode();
    }

    this.setSnbAudienceToStore(this.getSnbAudience());

    return queryParams;
  }

  /**
   * @description Resolves the snbAudience value from multiple sources.
   * Returns the first non-empty value found from: store value, window global, or request header.
   */
  getSnbAudience(): string {
    let snbAudience = '';
    if (this.snbAudience()) {
      // get the snb-audience directly from the store
      // this is the typical path for subsequent page loads
      return this.snbAudience();
    } else if (isPlatformBrowser(this.platformId) && globalThis.snbAudienceGlobal) {
      // get the snb-audience from the window object and set it in the store
      // this is the typical path for SRLP page load
      snbAudience = globalThis.snbAudienceGlobal;
    } else if (this.isFamilyPage()) {
      // get the snb-audience from the request header and set it in the store
      // this is the typical path for the first time the user hits the PLP
      snbAudience = this.httpService.getRequestHeaderValueAndSetStateKey('snb-audience') || '';
    }

    return snbAudience;
  }

  // Set the snbAudience value in the store if it's different from the current value
  setSnbAudienceToStore(snbAudience: string): void {
    if (snbAudience !== this.snbAudience()) {
      this.store.dispatch(snbAudienceChanged({ snbAudience }));
    }
  }

  getProductListDataFromApi$(
    queryParams: ProductCatalogInput,
    retries = 0,
  ): Observable<ProductList | null> {
    const url = this.environment.api.productList;
    const params = encodeURIComponent(JSON.stringify(queryParams));
    const requestUrl = `${url}v2/search?searchVO=${params}`;

    let httpHeaders = new HttpHeaders({
      ['X-TIMEOUT']: '7500',
      ['Content-Type']: MediaType.APPLICATION_JSON,
      channel: this.chain.chainIdentifierAbbr,
    });

    if (isPlatformBrowser(this.platformId) && !queryParams.isFamilyPage) {
      const poolCSwimlane: number | undefined =
        (globalThis._dsgTag?.customerLanes?.length ?? 0) >= 3
          ? globalThis._dsgTag?.customerLanes[2]
          : undefined;
      httpHeaders = httpHeaders.set(
        'pool-c-swimlane',
        `${poolCSwimlane && poolCSwimlane >= 0 ? poolCSwimlane : 0}`,
      );
    }

    if (url?.includes('prodauth')) {
      httpHeaders = httpHeaders.set('preview', 'rule');
    }

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
    }

    const httpOptions = {
      headers: httpHeaders,
    };

    return this.http.get<ProductList | null>(requestUrl, httpOptions).pipe(
      retry(retries),
      timeout(10000),
      catchError((error) => throwError(() => error)),
    );
  }

  searchRequestParamValidator(queryParams: Params): string {
    const validSearchTypes = new Set([
      'Keyword',
      'Category',
      'Manufacturer',
      'Suggested+Keyword',
      'Suggested Keyword',
      'History',
    ]);
    const validateParams = {
      catalogId: {
        validate: (value: string) => this.chain.catalogIdSales === value,
      },
      storeId: {
        validate: (value: string) => this.chain.storeId === value,
      },
      langId: { validate: (value: string) => '-1' === value },
      sType: { validate: (value: string) => 'SimpleSearch' === value },
      resultCatEntryType: { validate: (value: string) => '2' === value },
      showResultsPage: { validate: (value: string) => 'true' === value },
      fromPage: { validate: (value: string) => 'Search' === value },
      searchSource: { validate: (value: string) => 'Q' === value },
      pageView: { validate: (value: string) => '' === value },
      beginIndex: { validate: (value: string) => '0' === value },
      categoryId: { validate: (value: string) => '' === value },
      pageSize: {
        validate: (value: string) =>
          Number.parseInt(value, 10) >= 0 && Number.parseInt(value, 10) <= 145,
      },
      SearchType: { validate: (value: string) => validSearchTypes.has(value) },
      DSGSearchType: { validate: (value: string) => validSearchTypes.has(value) },
      advsearch: { validate: (value: string) => 'true' === value },
      selectedStore: {
        validate: (value: string) =>
          Number.parseInt(value, 10) >= 0 && Number.parseInt(value, 10) <= 999999,
      },
    };

    const errorMessages = [];

    if (!queryParams.searchTerm) {
      errorMessages.push('searchTerm is required');
    }

    /* linter flagged this as an error: Consider using "forEach" instead of "map" as its return value is not being used here */
    (Object.keys(validateParams) as Array<keyof typeof validateParams>).forEach((validParam) => {
      if (queryParams[validParam]) {
        const isValueValid = validateParams[validParam].validate(queryParams[validParam]);
        if (!isValueValid) {
          errorMessages.push(`${validParam} is invalid`);
        }
      }
    });
    return errorMessages.join('|');
  }

  /** Two-Pass Rendering **/

  private readonly PRODUCT_LIST_API_RESPONSE_KEY: StateKey<ProductList> = makeStateKey(
    'PRODUCT_LIST_API_RESPONSE',
  );

  getProductListFromTransferState(): ProductList | null {
    return this.transferState.get(this.PRODUCT_LIST_API_RESPONSE_KEY, null);
  }

  /**
   * Starts the first pass of getting product list data.
   * Server-side, it gets the API data and sets it into the transfer state.
   * Client-side, it gets the data from the transfer state.
   */
  getInitialProductListData$(
    queryParams: ProductCatalogInput,
    retries = 0,
  ): Observable<ProductList | null> {
    if (isPlatformBrowser(this.platformId)) {
      const transferStateProductList = this.getProductListFromTransferState();
      if (transferStateProductList) {
        transferStateProductList.fromTransferState = true;
        return of(transferStateProductList);
      }
    }
    return this.getProductListDataFromApi$(queryParams, retries).pipe(
      tap((response) => {
        this.transferState.set(this.PRODUCT_LIST_API_RESPONSE_KEY, response);
      }),
    );
  }

  /**
   * Marks the second pass as ready so that the product list data can be emitted and the product list updated.
   * Should only be called once, once the necessary component(s) are hydrated.
   */
  markSecondPassReady() {
    if (this.secondPassReady.closed) {
      this.logger.warn(
        'ProductListApiService: markSecondPassReady() was called more than once. Ignoring.',
      );
      return;
    }
    this.secondPassReady.next();
    this.secondPassReady.complete();
    this.secondPassReady.unsubscribe();
  }

  /**
   * Starts the second pass of getting product list data.
   * Waits for the API call to complete and for the second pass to be marked as ready before emitting the product list data.
   * Should only be called client-side; returns null server-side.
   */
  getSecondPassProductListData$(
    queryParams: ProductCatalogInput,
    retries = 0,
  ): Observable<ProductList | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const apiCall$ = this.getProductListDataFromApi$(queryParams, retries).pipe(shareReplay(1));

    return zip([apiCall$, this.secondPassReady]).pipe(map(([productList]) => productList));
  }

  /**
   * Formats and logs a SearchVO API error with a source tag, timeout prefix,
   * and structured details including the current page URL (X-HREF).
   *
   * @param error The error object to be logged.
   * @param source The source tag indicating where the error originated.
   * @param context The context of the error, either a search term or a category group ID.
   */
  logProductListApiError(
    error: unknown,
    source: ProductListApiErrorSource,
    context: { searchTerm?: string | null; catgroupId?: number },
  ): void {
    const err: ProductListApiErrorLike = typeof error === 'object' && error !== null ? error : {};

    const isTimeout = typeof err.name === 'string' && err.name.toLowerCase() === 'timeouterror';

    const { global } = DCSG_CONSTANTS.errorHandling.messagePrefixes;
    const prefix = isTimeout ? global.timeout : global.http;

    const contextPart = context?.searchTerm
      ? `searchTerm: "${context.searchTerm}"`
      : `catgroupId: ${context?.catgroupId ?? ''}`;

    const message = `${prefix}[${source}] Search API failure — ${contextPart} ${err.message ?? ''}`;

    this.logger.error(message, {
      'X-ERROR-STATUS': `${err.status ?? ''}`,
      'X-ERROR-MESSAGE': `${err.message ?? ''}`,
      'X-ERROR-NAME': `${err.name ?? ''}`,
      'X-ERROR-STACK': `${err.stack ?? ''}`,
      'X-HREF': this.buildErrorHref(),
    });
  }

  private buildErrorHref(): string {
    const queryString = this.httpService.getQueryString();
    return `https://${this.httpService.getHostName()}${this.httpService.getUrlPath()}${
      queryString ? `?${queryString}` : ''
    }`;
  }
}
